// Document identity is deliberately NOT copied through sessionStorage on tab
// duplication/reload. Sequence numbers order pagehide/pageshow/in-flight signals.
const tabId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
export const documentHeaders = () => ({ 'X-Insight-Document': tabId });
const handoffKey = 'insightReloadHandoff';
let reloadHandoff = sessionStorage.getItem(handoffKey);
sessionStorage.removeItem(handoffKey);
let admittedToken = null;
let admission;
let departedOnce = false;
let sequence = 0;
export async function admitBrowserDocument(base, token) {
  if (admittedToken === token) return;
  if (admission) return admission;
  let handoff = sessionStorage.getItem(handoffKey) || reloadHandoff;
  sessionStorage.removeItem(handoffKey);
  admission = (async () => {
    // pagehide's keepalive may reach the server after the new document starts.
    for (let attempt = 0; attempt < 12; attempt++) {
      // Chromium can restore a cached document before its sessionStorage view
      // sees the departing document's final write. Re-read during bounded grace.
      handoff = sessionStorage.getItem(handoffKey) || handoff;
      sessionStorage.removeItem(handoffKey);
      reloadHandoff = handoff;
      if (departedOnce && !handoff && attempt < 11) {
        await new Promise(resolve => setTimeout(resolve, 150)); continue;
      }
      const response = await fetch(`${base}/auth/admit`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, ...documentHeaders(),
          ...(handoff ? { 'X-Insight-Handoff': handoff } : {}) }, cache: 'no-store',
      });
      if (response.ok) { admittedToken = token; reloadHandoff = null; departedOnce = false; return; }
      const data = await response.json();
      if ((handoff || departedOnce) && response.status === 409 && attempt < 11) {
        await new Promise(resolve => setTimeout(resolve, 150)); continue;
      }
      const error = new Error(data.detail?.message || data.detail || 'Session unavailable');
      error.response = { status: response.status, data };
      throw error;
    }
  })();
  try { await admission; } finally { admission = null; }
}
let activeToken = null;
let api;
let timer;
let hiddenPage = false;
let interval = 15000;

async function signal(departed = false) {
  const token = activeToken;
  if (!token) return;
  let handoff;
  if (departed) {
    handoff = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(handoffKey, handoff);
  } else sessionStorage.removeItem(handoffKey);
  const body = JSON.stringify({ tab_id: tabId, sequence: ++sequence, departed, handoff });
  const controller = new AbortController();
  const timeout = departed ? null : setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(`${api}/auth/presence`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...documentHeaders() },
      body, keepalive: departed, cache: 'no-store', credentials: 'same-origin', signal: controller.signal,
    });
    if (token !== activeToken) return;
    if (response.status === 401 && !departed) {
      stopBrowserPresence();
      window.dispatchEvent(new CustomEvent('insight-session-expired', { detail: { token } }));
    } else if (response.status === 409 && !departed) {
      const data = await response.json();
      blockDocument(token, data.detail?.message);
    } else if (response.ok && !departed) {
      const data = await response.json();
      interval = data.heartbeat_seconds * 1000 || 15000;
    }
  } catch (_) { /* Bounded server lease handles missing signals; never revive it locally. */ }
  finally { clearTimeout(timeout); }
}
function schedule() {
  clearTimeout(timer);
  if (activeToken && !hiddenPage) timer = setTimeout(async () => { await signal(); schedule(); }, interval);
}
let cover;
function coverDocument() {
  if (cover) return;
  cover = document.createElement('div');
  cover.style.cssText = 'position:fixed;inset:0;z-index:2147483647;background:white;display:grid;place-items:center;color:#334155';
  cover.textContent = 'Checking your INSIGHT session…';
  document.body.appendChild(cover);
}
function uncoverDocument() { cover?.remove(); cover = null; }
function blockDocument(token, message) {
  coverDocument();
  stopBrowserPresence();
  window.dispatchEvent(new CustomEvent('insight-tab-blocked', { detail: { token, message } }));
  requestAnimationFrame(() => requestAnimationFrame(uncoverDocument));
}
function depart() {
  if (!activeToken) return;
  coverDocument();
  admittedToken = null;
  departedOnce = true;
  hiddenPage = true;
  clearTimeout(timer);
  void signal(true);
}
function resume() {
  hiddenPage = false;
  if (!activeToken) return;
  const token = activeToken;
  void admitBrowserDocument(api, token).then(async () => {
    if (token !== activeToken) return;
    await signal();
    if (token === activeToken) { schedule(); uncoverDocument(); }
  }).catch(error => {
    if (error.response?.status === 409) blockDocument(token, error.response.data.detail.message);
    else if (error.response?.status === 401) {
      stopBrowserPresence();
      window.dispatchEvent(new CustomEvent('insight-session-expired', { detail: { token } }));
      requestAnimationFrame(() => requestAnimationFrame(uncoverDocument));
    } else if (token === activeToken) timer = setTimeout(resume, interval);
  });
}
function visible() {
  // Hiding/minimizing is not departure; background heartbeats continue.
  if (document.visibilityState === 'visible') resume();
}
export async function startBrowserPresence(base, token) {
  await admitBrowserDocument(base, token);
  api = base;
  if (activeToken !== token) {
    stopBrowserPresence();
    activeToken = token;
    hiddenPage = false;
    window.addEventListener('pagehide', depart);
    window.addEventListener('pageshow', resume);
    window.addEventListener('online', resume);
    document.addEventListener('visibilitychange', visible);
  }
  await signal();
  schedule();
}
export function stopBrowserPresence() {
  activeToken = null;
  clearTimeout(timer);
  window.removeEventListener('pagehide', depart);
  window.removeEventListener('pageshow', resume);
  window.removeEventListener('online', resume);
  document.removeEventListener('visibilitychange', visible);
}
